import { useMemo } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Form,
  FormFeedback,
  Input,
  Label,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import toastr from "toastr";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useUpdatePassword } from "../../api/auth";
import { performLogout } from "../../helpers/performLogout";
import { mediaUrl } from "../../helpers/mediaHelper";
import { selectUser } from "../../store/auth/selectors";

const PRIVACY_POLICY_URL = "https://www.jobark.co.uk/privacy-policy/";
const SECONDARY = "var(--secondary, #26a79a)";

function AccountSettingsPage({
  showAgreement,
  documentTitleKey,
  documentTitleDefault,
  t,
}) {
  document.title = t(documentTitleKey, documentTitleDefault);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);
  const updatePassword = useUpdatePassword();

  const agreementUrl = useMemo(() => {
    if (!showAgreement) return "";
    const proof = user?.worker?.declarationsConsents?.declarationProof;
    return proof ? mediaUrl(proof) : "";
  }, [showAgreement, user]);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required(
        t(
          "workerSettingsCurrentPasswordRequired",
          "Please enter your current password."
        )
      ),
      password: Yup.string()
        .min(8, t("resetPasswordMinChars"))
        .required(
          t(
            "workerSettingsNewPasswordRequired",
            "Please enter a new password."
          )
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], t("resetPasswordMustMatch"))
        .required(
          t(
            "workerSettingsConfirmPasswordRequired",
            "Please confirm your new password."
          )
        ),
    }),
    onSubmit: (values, { resetForm }) => {
      updatePassword.mutate(
        {
          currentPassword: values.currentPassword,
          password: values.password,
          confirmPassword: values.confirmPassword,
        },
        {
          onSuccess: () => {
            toastr.success(
              t(
                "workerSettingsPasswordUpdated",
                "Password updated successfully."
              )
            );
            resetForm();
          },
        }
      );
    },
  });

  const handleLogout = () => {
    performLogout({ navigate, queryClient });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={t("workerMenuSettings", "Settings")}
          breadcrumbItem={t("workerMenuSettings", "Settings")}
        />

        <Card className="mb-4">
          <CardBody>
            <h5 className="fw-bold mb-4">
              {t("workerSettingsAccountSecurity", "Account Security")}
            </h5>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit();
                return false;
              }}
            >
              <div className="mb-3">
                <Label className="form-label" htmlFor="currentPassword">
                  {t("workerSettingsCurrentPassword", "Current Password")}
                </Label>
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.currentPassword &&
                    !!formik.errors.currentPassword
                  }
                />
                <FormFeedback>{formik.errors.currentPassword}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label className="form-label" htmlFor="password">
                  {t("workerSettingsNewPassword", "New Password")}
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.password && !!formik.errors.password}
                />
                <FormFeedback>{formik.errors.password}</FormFeedback>
              </div>

              <div className="mb-4">
                <Label className="form-label" htmlFor="confirmPassword">
                  {t("workerSettingsConfirmPassword", "Confirm Password")}
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.confirmPassword &&
                    !!formik.errors.confirmPassword
                  }
                />
                <FormFeedback>{formik.errors.confirmPassword}</FormFeedback>
              </div>

              <Button
                type="submit"
                style={{ background: SECONDARY, borderColor: SECONDARY }}
                disabled={updatePassword.isPending}
              >
                {updatePassword.isPending
                  ? t("workerSettingsUpdatingPassword", "Updating…")
                  : t("workerSettingsUpdatePassword", "Update Password")}
              </Button>
            </Form>
          </CardBody>
        </Card>

        <Card className="mb-4">
          <CardBody>
            <h5 className="fw-bold mb-4">
              {t("workerSettingsLegal", "Legal")}
            </h5>

            <div className={showAgreement ? "mb-4" : "mb-0"}>
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noreferrer"
                className="fw-medium"
              >
                {t("workerSettingsPrivacyPolicy", "Privacy Policy")}
              </a>
            </div>

            {showAgreement && (
              <div>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <h6 className="mb-0">
                    {t("agreementsWorkerAgreement", "Worker Agreement")}
                  </h6>
                  {agreementUrl ? (
                    <a
                      href={agreementUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-secondary"
                    >
                      {t("workerSettingsOpenInNewTab", "Open in new tab")}
                    </a>
                  ) : null}
                </div>

                {agreementUrl ? (
                  <iframe
                    title={t("agreementsWorkerAgreement", "Worker Agreement")}
                    src={agreementUrl}
                    className="account-settings-agreement-frame"
                    allow="clipboard-read; clipboard-write; fullscreen; display-capture"
                    allowFullScreen
                  />
                ) : (
                  <Alert color="light" className="mb-0 text-muted">
                    {t(
                      "workerSettingsNoAgreement",
                      "No worker agreement is available."
                    )}
                  </Alert>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        <div className="d-grid d-md-block mb-4">
          <Button color="danger" outline onClick={handleLogout}>
            <i className="bx bx-power-off me-1" />
            {t("Logout", "Logout")}
          </Button>
        </div>
      </div>
    </div>
  );
}

AccountSettingsPage.propTypes = {
  showAgreement: PropTypes.bool,
  documentTitleKey: PropTypes.string,
  documentTitleDefault: PropTypes.string,
  t: PropTypes.func,
};

AccountSettingsPage.defaultProps = {
  showAgreement: false,
  documentTitleKey: "workerSettingsTitle",
  documentTitleDefault: "Settings | Worker",
};

export default withTranslation()(AccountSettingsPage);
